<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { useDashboardStore } from '../dashboard/store/useDashboardStore'
import { can } from '@/@layouts/plugins/casl'
import LayoutTable from '@/components/CDF/LayoutTable.vue'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import { useUsersStore } from '@/pages/admin/users/store/useUsersStore'
import type { IUser } from '@/pages/admin/users/types'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import { getI18n } from '@/plugins/i18n'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

const i18n = getI18n()
const { t } = useI18n(i18n)

definePage({
  meta: {
    action: 'list',
    subject: 'users',
  },
})

const store = useUsersStore()
const dashboardStore = useDashboardStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
} = storeToRefs(store)

const {
  userCounts,
} = storeToRefs(dashboardStore)

const {
  fetchUserCounts,
} = dashboardStore

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  dialogDestroy,
  onOrderBy,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: t('users.headers.name'),
      key: 'name',
    },
    {
      title: t('users.headers.email'),
      key: 'email',
    },
    {
      title: 'Perfil',
      key: 'role',
      sortable: false,
    },
  ]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: t('users.terms.name'),
      value: 'name',
    },
    {
      title: t('users.terms.email'),
      value: 'email',
    },
    {
      title: 'Perfil',
      value: 'roles.slug',
      relationship: 'belongsToManyRoles',
      type: 'SELECT',
      options: [
        {
          title: 'Fornecedor',
          value: 'fornecedor',
        },
        {
          title: 'Espaço Eventos',
          value: 'espaco_evento',
        },
        {
          title: 'Organizador',
          value: 'organizador',
        },
      ],
    },
  ] as ITerm[]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'users',
    },
    to: (item: IUser) => `/admin/users/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'users',
    },
    onClick: (item: IUser) => dialogDestroy(item.id),
  },
]

onBeforeRouteLeave(() => {
  store.$reset()
})

onMounted(() => {
  fetchUserCounts()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :is-header="isHeader"
    :destroy="destroy"
    :title="$t('users.list')"

    new-item="/admin/users/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #content>
      <VCard class="my-2">
        <VCardText>
          <VRow class="my-0">
            <VCol
              cols="12"
              md="4"
            >
              <div class="gap-4 mt-0 d-flex align-center">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  rounded
                  size="40"
                >
                  <VIcon icon="tabler-truck-delivery" />
                </VAvatar>

                <div class="d-flex flex-column">
                  <h5 class="text-h5">
                    {{ userCounts.fornecedor || 0 }}
                  </h5>
                  <div class="text-sm">
                    Fornecedor
                  </div>
                </div>
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <div class="gap-4 mt-0 d-flex align-center">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  rounded
                  size="40"
                >
                  <VIcon icon="tabler-building" />
                </VAvatar>

                <div class="d-flex flex-column">
                  <h5 class="text-h5">
                    {{ userCounts.espaco_evento || 0 }}
                  </h5>
                  <div class="text-sm">
                    Espaço Eventos
                  </div>
                </div>
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <div class="gap-4 mt-0 d-flex align-center">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  rounded
                  size="40"
                >
                  <VIcon icon="tabler-calendar-event" />
                </VAvatar>

                <div class="d-flex flex-column">
                  <h5 class="text-h5">
                    {{ userCounts.organizador || 0 }}
                  </h5>
                  <div class="text-sm">
                    Organizador
                  </div>
                </div>
              </div>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </template>

    <template #actions="{ item }">
      <CDFActionButton
        v-if="can('block', 'users')"
        :icon="item.ativo ? 'tabler-user-off' : 'tabler-user-check'"
        :color="item.ativo ? 'error' : 'success'"
        @click="() => {
          store.blockOrUnblockUser(item.id)
        }"
      />
    </template>
    <template #item.name="{ item }">
      <div class="d-flex align-center gap-x-2">
        <CDFAvatar
          size="32"
          color="primary"
          :foto="item.foto"
          :name="item.name"
        />
        {{ item.name }}
      </div>
    </template>
    <template #item.role="{ item, index }">
      <VChip
        :key="index"
        density="comfortable"
        size="small"
      >
        {{ item.role?.name }}
      </VChip>
    </template>
  </LayoutTable>
</template>
